import { Link } from "react-router-dom";
import { Reveal } from "@/components/motion/Reveal";
import { Instagram, ArrowUpRight } from "lucide-react";
import {
  KINGSLEY_LOGO_INK,
  KINGSLEY_INSTAGRAM,
  KINGSLEY_INSTAGRAM_HANDLE,
} from "@/data/media";
import { useStore } from "@/context/StoreContext";
import { imgSrc } from "@/lib/api";

const HOW = [
  { k: "01", t: "Made for you", d: "Choose the colour, length and neckline, and it’s cut to your size — the dress you want, made yours." },
  { k: "02", t: "Customizable, not custom", d: "Not bespoke — considered pieces with a few choices that make each one your own." },
  { k: "03", t: "Ready to wear", d: "A handful of one-off and in-stock pieces are there to take exactly as they are." },
];

export default function Kingsley() {
  const { categories, site_images } = useStore();
  const SIGNATURE = site_images.kingsley_signature || [];
  const ATELIER = site_images.kingsley_atelier || [];
  return (
    <div className="bg-[#EDECE9]">
      {/* Hero */}
      <section data-testid="kingsley-hero" className="relative flex min-h-[86vh] flex-col justify-center bg-[#E7E6E2] px-6 pb-14 pt-36 md:px-12 md:pt-40">
        <div className="mx-auto w-full max-w-[1500px]">
          <Reveal>
            <p className="overline mb-8 flex items-center gap-3 text-muted">
              <span className="h-px w-10 bg-clay" />
              The boutique · a line by Phaedra Paulson
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <h1><img src={KINGSLEY_LOGO_INK} alt="M. Kingsley Studio" data-testid="kingsley-logo" width="820" height="109" className="w-[min(820px,92vw)]" /></h1>
          </Reveal>
          <Reveal delay={0.3}>
            <p className="mt-10 max-w-md font-body text-sm leading-relaxed text-stone">
              A small line — the dress you want, made for you. Every piece is made by hand by Phaedra —
              the jewelry and hand-dyed pieces are each one of a kind. Step in and have a look, or come in for a fitting.
            </p>
          </Reveal>
          <Reveal delay={0.42}>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link
                to="/kingsley/c/dresses"
                data-testid="kingsley-cta-shop"
                className="group inline-flex items-center gap-3 bg-ink px-9 py-4 text-paper transition-colors duration-500 hover:bg-ink/85"
              >
                <span className="overline">Shop the line</span>
                <span className="transition-transform duration-500 group-hover:translate-x-1.5">↗</span>
              </Link>
              <Link
                to="/kingsley/fitting"
                data-testid="kingsley-cta-fitting"
                className="group inline-flex items-center gap-3 border border-ink px-9 py-4 text-ink transition-colors duration-500 hover:bg-ink hover:text-paper"
              >
                <span className="overline">Book a fitting</span>
              </Link>
              <a href={KINGSLEY_INSTAGRAM} target="_blank" rel="noreferrer" data-testid="kingsley-instagram-hero" className="group inline-flex items-center gap-2.5 text-stone transition-colors duration-300 hover:text-ink">
                <Instagram className="h-4 w-4" strokeWidth={1.5} />
                <span className="overline">{KINGSLEY_INSTAGRAM_HANDLE}</span>
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Signature looks — a taste, not everything */}
      <section data-testid="kingsley-signature" className="bg-[#EAEAE7] px-6 py-16 md:px-12 md:py-24">
        <div className="mx-auto grid max-w-[1500px] grid-cols-2 gap-4 md:grid-cols-3 md:gap-7">
          {SIGNATURE.map((slug, i) => (
            <Link
              key={slug}
              to="/kingsley/c/dresses"
              data-testid={`kingsley-signature-${i}`}
              className={`group relative block overflow-hidden bg-[#333336] ${i === 2 ? "col-span-2 md:col-span-1" : ""}`}
            >
              <img src={imgSrc(slug)} alt="Kingsley signature look" className="hover-media aspect-[3/4] w-full object-cover object-top" />
            </Link>
          ))}
        </div>
      </section>

      {/* The Collection — rooms */}
      <section data-testid="kingsley-rooms" className="bg-[#EDECE9] px-6 py-20 md:px-12 md:py-28">
        <div className="mx-auto max-w-[1500px]">
          <Reveal>
            <p className="overline text-muted">(The Collection)</p>
            <h2 className="mt-4 max-w-2xl font-display text-4xl font-extralight leading-[1.02] tracking-tight text-ink md:text-6xl">
              Browse the line by the piece.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 md:gap-8">
            {categories.map((c, i) => (
              <Reveal key={c.id} delay={i * 0.06}>
                <Link to={`/kingsley/c/${c.id}`} data-testid={`kingsley-room-${c.id}`} className="group block">
                  <div className="relative overflow-hidden bg-[#333336]">
                    <img src={imgSrc(c.cover)} alt={c.title} className="hover-media aspect-[4/3] w-full object-cover object-top" />
                  </div>
                  <div className="mt-4 flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-display text-2xl font-extralight tracking-tight text-ink md:text-3xl">{c.title}</h3>
                      <p className="mt-2 max-w-sm font-body text-sm leading-relaxed text-stone">{c.note}</p>
                    </div>
                    <ArrowUpRight className="mt-1 h-5 w-5 shrink-0 text-clay transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* In the atelier — the subtle "she makes it all" note */}
      <section data-testid="kingsley-atelier" className="bg-[#F2F1EE] px-6 py-16 md:px-12 md:py-24">
        <div className="mx-auto max-w-[1500px]">
          <Reveal>
            <p className="overline text-muted">(In the atelier)</p>
            <p className="mt-4 max-w-xl font-display text-2xl font-extralight leading-snug tracking-tight text-ink md:text-3xl">
              Every piece is cut, draped and sewn by hand — by Phaedra herself.
            </p>
          </Reveal>
          <div className="mt-10 grid grid-cols-3 gap-3 md:gap-5">
            {ATELIER.map((slug, i) => (
              <Reveal key={slug} delay={i * 0.06}>
                <div className="overflow-hidden bg-[#333336]">
                  <img src={imgSrc(slug)} alt="In the Kingsley atelier" className="aspect-[3/4] w-full object-cover object-top opacity-95" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>


      {/* How it works */}
      <section data-testid="kingsley-how" className="border-t border-hairline bg-[#F7F5F2] px-6 py-20 md:px-12 md:py-28">
        <div className="mx-auto max-w-[1500px]">
          <Reveal>
            <p className="overline text-muted">(How it works)</p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-10 md:grid-cols-3 md:gap-16">
            {HOW.map((h, i) => (
              <Reveal key={h.k} delay={i * 0.08}>
                <span className="font-mono text-xs tracking-[0.35em] text-clay">{h.k}</span>
                <h3 className="mt-4 font-display text-2xl font-extralight tracking-tight text-ink md:text-3xl">{h.t}</h3>
                <p className="mt-4 font-body text-sm leading-relaxed text-stone">{h.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Book a fitting — CTA band */}
      <section data-testid="kingsley-fitting-cta" className="border-t border-hairline bg-[#211E1C] px-6 py-24 md:px-12 md:py-32">
        <div className="mx-auto flex max-w-[1100px] flex-col items-center text-center">
          <Reveal>
            <p className="overline text-paper/80">(Come in for a fitting)</p>
            <h2 className="mt-5 font-display text-3xl font-extralight leading-[1.1] tracking-tight text-[#EDECE9] md:text-5xl">
              See it, feel it, have it made for you.
            </h2>
            <p className="mx-auto mt-6 max-w-md font-body text-sm leading-relaxed text-paper/90">
              Book a fitting — in person or over video — and answer a few quick questions about
              what you’re looking for.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <Link
              to="/kingsley/fitting"
              data-testid="kingsley-fitting-cta-btn"
              className="group mt-10 inline-flex items-center gap-3 bg-paper px-10 py-4 text-ink transition-transform duration-500 hover:scale-[1.02]"
            >
              <span className="overline">Book a fitting</span>
              <span className="transition-transform duration-500 group-hover:translate-x-1.5">↗</span>
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
